import { Flags } from "@/feature-flags";
import { useExperimentEnabled } from "@/hooks/use-experiment";
import { useOnaEnabledForOrg } from "@/hooks/use-ona-enabled";
import { useGetAccount } from "@/queries/account-queries";
import { useOrganization } from "@/queries/organization-queries";
import { useFeatureFlag, type SettingTypeOf, type SettingValue } from "configcat-react";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

export function useFlag<T extends SettingValue>(
    flagKey: string,
    defaultValue: T,
): { value: SettingTypeOf<T>; loading: boolean } {
    const { data: account } = useGetAccount();
    const { data: organization } = useOrganization();

    const { value, loading } = useFeatureFlag<T>(
        flagKey,
        defaultValue,
        account
            ? {
                  custom: {
                      organization_id: organization?.id ?? "",
                      organization_tier: OrganizationTier[organization?.tier ?? OrganizationTier.UNSPECIFIED],
                  },
                  identifier: account.id,
                  email: account.email,
              }
            : undefined,
    );

    return { value, loading };
}

// Helper methods to OR feature flags and useOnaEnabled
// TODO(gpl): Team Agent plans to consolidate these soon
export const useFlagOnaAIEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.OnaAIEnabled, false);
    const { value: tierEnabled, loading: tierLoading } = useOnaEnabledForOrg();

    return {
        value: flagEnabled || tierEnabled,
        loading: flagLoading || tierLoading,
    };
};

export const useFlagOnaEnvironmentOnboardingEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.OnaEnvironmentOnboardingEnabled, false);
    const { value: tierEnabled, loading: tierLoading } = useOnaEnabledForOrg();

    return {
        value: flagEnabled || tierEnabled,
        loading: flagLoading || tierLoading,
    };
};

export const useFlagIsSentryEnabled = () => {
    return useFlag(Flags.IsSentryEnabled, false);
};

export const useFlagIsMobileVscodeEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.IsMobileVscodeEnabled, true);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

// TODO(mads): Move to be a capability in frontend/dashboard/src/hooks/use-feature-automations.ts
export const useFlagAutomationsRepoListModeEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.AutomationsRepoListModeEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagShareableOnaLinksEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.ShareableOnaLinksEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagSCIMEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.SCIMEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagAgentImageAttachmentsEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.AgentImageAttachmentsEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagAcpAgentsEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.AcpAgentsEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagDevRunnerRegistrationEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.DevRunnerRegistrationEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagSSOAdditionalScopesEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.SSOAdditionalScopesEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagVetoExecutions = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.VetoExecutions, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagCommandPaletteEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.CommandPaletteEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagRunnerSideAgentEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.RunnerSideAgentEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagSuperOnaEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.SuperOnaEnabled, false);
    const experimentEnabled = useExperimentEnabled("superOna");

    return {
        value: flagEnabled && experimentEnabled,
        loading: flagLoading,
    };
};

export const useFlagScopedEventStreamsEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.ScopedEventStreamsEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagWarmPoolsEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.WarmPoolsEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagChangelogWidgetEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.ChangelogWidgetEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagFargateRunnerEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.FargateRunnerEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagProjectInsightsEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.ProjectInsightsEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagAutomationsRedesignEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.AutomationsRedesignEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagPylonWebChatDisabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.PylonWebChatDisabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagSessionsEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.SessionsEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagTeamsEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.TeamsEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagPortAuthenticationEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.PortAuthenticationEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagCreditUsagePreaggregation = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.UseCreditUsagePreaggregation, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};
